import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pickLandingPath } from '../shared/roles';
import { Dumbbell, ArrowRight, ChevronDown, Shield, Utensils, AlertTriangle, Brain, Activity, CheckCircle2, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import FMLogo from '../components/FMLogo';

const fade = { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } };

const FEATURES = [
  { icon: Dumbbell, title: 'Smart Workout Plans', desc: 'Injury-aware routines built for your body and goals.' },
  { icon: Utensils, title: 'Smart Nutrition', desc: 'Allergy-safe meal plans with macro tracking and swap.' },
  { icon: Shield, title: 'Real Data Only', desc: 'No fake progress. Every stat is user-triggered.' },
];

const AI_RISKS = [
  {
    icon: AlertTriangle,
    title: 'Chatbots hallucinate',
    desc: 'Generic AI invents rep counts, calorie totals, and exercise names that do not exist. Your body pays the cost.',
  },
  {
    icon: Brain,
    title: 'They forget you',
    desc: 'Every chat starts from zero. No memory of last week’s workout, no progressive overload, no real plan.',
  },
  {
    icon: Activity,
    title: 'They ignore your limits',
    desc: 'No knowledge of your knee injury, your peanut allergy, or your goal. One-size-fits-none advice.',
  },
];

const PLATFORM_PROOF = [
  'Deterministic plan engine — no hallucinated reps, every set is grounded in a rule we can show you.',
  'Indian-first nutrition — vegetarian, vegan, and 14 allergens handled by the meal generator, not by hope.',
  'Real progress memory — every set, swap, and weight log informs next week’s plan.',
  'Built with trainers and tested with real users in beta.',
];

export default function LandingPage() {
  const { login, user, loading, loginError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !user) return;
    navigate(pickLandingPath(user), { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white overflow-hidden">
      <main>
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col justify-center px-6 md:px-12" data-testid="hero-section">
        <div className="absolute inset-0 bg-[#0B0B0B]">
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle, #C6A85B 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        </div>

        <div className="relative z-10 max-w-lg mx-auto w-full text-center">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.12 } } }}>
            <motion.div variants={fade} className="mb-8">
              <FMLogo className="w-48 h-28 mx-auto" />
            </motion.div>

            <motion.h1 variants={fade} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
              Your coach.<br />
              <span className="text-[#C6A85B]">Your plan.</span><br />
              Your pace.
            </motion.h1>

            <motion.p variants={fade} className="font-body text-base text-[#C9C9C9] leading-relaxed mb-3 max-w-sm mx-auto">
              Workouts that fit your body. Meals that fit your goals. Progress you can actually see.
            </motion.p>
            <motion.p variants={fade} className="font-body text-sm text-[#A3A3A3] leading-relaxed mb-10 max-w-sm mx-auto">
              Built for serious lifters in India — no fad templates, no recycled gym-bro advice.
            </motion.p>

            <motion.div variants={fade} className="space-y-3">
              <button
                onClick={login}
                data-testid="hero-login-btn"
                className="w-full bg-[#C6A85B] text-[#0B0B0B] hover:bg-[#D4B972] transition-colors duration-200 h-14 rounded-xl font-heading font-semibold text-lg flex items-center justify-center gap-2"
              >
                Start free <ArrowRight className="w-5 h-5" />
              </button>
              <p className="font-body text-xs text-[#A3A3A3] tracking-wide">Sign in with Google. Upgrade only when you're ready.</p>
              {loginError && (
                <p className="font-body text-xs text-red-400 text-center px-2">{loginError}</p>
              )}
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-5 h-5 text-[#A3A3A3]" />
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6 md:px-12" data-testid="features-section">
        <div className="max-w-lg mx-auto">
          <p className="text-[#C6A85B] font-body text-xs uppercase tracking-[0.2em] font-semibold mb-2">Why Fitness Mitra</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-10">Not a tracker.<br />A trainer.</h2>

          <div className="space-y-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fade}
                className="bg-[#161616] border border-[#262626] rounded-2xl p-6 flex gap-4 items-start"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-10 h-10 bg-[#C6A85B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <f.icon className="w-5 h-5 text-[#C6A85B]" />
                </div>
                <div>
                  <h3 className="font-heading text-base font-medium mb-1">{f.title}</h3>
                  <p className="font-body text-sm text-[#A3A3A3] leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why not just an AI chatbot */}
      <section className="py-16 px-6 md:px-12 bg-[#0E0E0E] border-t border-[#1A1A1A]" data-testid="ai-comparison-section">
        <div className="max-w-lg mx-auto">
          <p className="text-[#C6A85B] font-body text-xs uppercase tracking-[0.2em] font-semibold mb-2">
            Built different
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-3">
            Why not just ask<br />an AI chatbot?
          </h2>
          <p className="font-body text-sm text-[#A3A3A3] leading-relaxed mb-10">
            Generic AI assistants were not built for your body. They guess. They forget. They harm.
          </p>

          <div className="space-y-3 mb-10">
            {AI_RISKS.map((risk, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fade}
                className="bg-[#161616] border border-[#262626] rounded-2xl p-5 flex gap-4 items-start"
                data-testid={`ai-risk-${i}`}
              >
                <div className="w-9 h-9 bg-[#E53935]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <risk.icon className="w-4 h-4 text-[#E53935]" />
                </div>
                <div>
                  <h3 className="font-heading text-sm font-semibold mb-1">{risk.title}</h3>
                  <p className="font-body text-xs text-[#A3A3A3] leading-relaxed">{risk.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fade}
            className="bg-[#161616] border border-[#C6A85B]/40 rounded-2xl p-6"
            data-testid="platform-proof-card"
          >
            <p className="text-[#C6A85B] font-body text-[10px] uppercase tracking-[0.2em] font-semibold mb-2">
              Fitness Mitra
            </p>
            <h3 className="font-heading text-lg font-semibold mb-4 leading-tight">
              Engineered, not improvised.
            </h3>
            <ul className="space-y-3">
              {PLATFORM_PROOF.map((item, i) => (
                <li key={i} className="flex gap-3 items-start" data-testid={`platform-proof-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-[#C6A85B] flex-shrink-0 mt-0.5" />
                  <span className="font-body text-sm text-[#E5E5E5] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>
      </main>

      <footer className="border-t border-[#262626] py-6 px-6 text-center" data-testid="footer">
        <FMLogo className="w-24 h-14" />
        <p className="font-body text-xs text-[#A3A3A3] mt-1">&copy; 2026 Fitness Mitra. Premium Fitness.</p>
      </footer>

      <div className="bg-[#0E0E0E] border-t border-[#161616] py-5 px-6 text-center" data-testid="footer-contact">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C6A85B] font-semibold mb-2">Contact</p>
        <a
          href="mailto:contactfitnessmitra@gmail.com"
          className="inline-flex items-center gap-2 text-sm text-[#E5E5E5] hover:text-[#C6A85B] transition-colors"
          data-testid="footer-contact-email"
        >
          <Mail className="w-4 h-4 text-[#C6A85B]" />
          contactfitnessmitra@gmail.com
        </a>
        <p className="mt-3 text-[11px] text-[#7A7A7A]">
          <a href="/terms" className="hover:text-[#C6A85B]" data-testid="footer-terms-link">
            Terms &amp; Conditions
          </a>
        </p>
      </div>
    </div>
  );
}
